// CPC DEX — DGC/DATA + CPC/DATA spot trade
import { useCurrentAccount, useSuiClient } from '@mysten/dapp-kit';
import {
	DATA_DGC_REFERENCE_DATA_PER_DGC,
	buildCancelOrder,
	buildPlaceAskForOwner,
	buildPlaceBidForOwner,
	clobBaseType,
	estimateCpcProceedsForDataAsk,
	filterMyDataCpcTrades,
	getCoinBalanceView,
	getCpcBalance,
	mistToDgc,
	mistToCoin,
	parseLimitPriceDgcPerData,
	quoteDgcMistForDataBid,
	verifyClobObjectsReachable,
	type ClobMarketConfig,
} from '@cpc/tx';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Text } from '@mysten/ui';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import clsx from 'clsx';

import { CpcKlineChart } from '~/components/trade/CpcKlineChart';
import { lastAt } from '~/utils/lastAt';
import { CpcTradeAssetsPanel } from '~/components/trade/CpcTradeAssetsPanel';
import { PageLayout } from '~/components/Layout/PageLayout';
import { TradeBottomPanel, type BottomTab } from '~/components/trade/TradeBottomPanel';
import { TradeMarketOverview } from '~/components/trade/TradeMarketOverview';
import { TradeOrderBook } from '~/components/trade/TradeOrderBook';
import { TradeOrderForm } from '~/components/trade/TradeOrderForm';
import { TradeRecentTrades } from '~/components/trade/TradeRecentTrades';
import {
	formatLastTradePrice,
	pctChange,
	parseQtyLots,
	sanitizeLimitPriceInput,
	sanitizeQtyInput,
	tryParseQtyLots,
	tradeCls,
	DEPTH_MAX_LEVELS,
	KLINE_CHART_HEIGHT_PX,
	computeTicker24h,
	computeBookTicker,
	resolveTradeDisplaySide,
	type KlineInterval,
} from '~/components/trade/tradeTheme';
import {
	fetchClobMarketsFromHub,
	getClobLotSizeMist,
	getClobMarketsFromVite,
	getPushHubWsUrl,
} from '~/config/clobConfig';
import { useCpcSignAndExecuteTransaction } from '~/hooks/useCpcSignAndExecuteTransaction';
import { usePushHub, type BookLevel } from '~/hooks/usePushHub';
import { filterHubOrdersForPair } from '~/hooks/pushHubClient';

type TradeSide = 'bid' | 'ask';

export default function CpcTradePage() {
	// Markets follow Hub stack-config (clob + clobCpcData); vite is offline fallback only.
	const marketsQ = useQuery({
		queryKey: ['hub-clob-markets'],
		queryFn: ({ signal }) => fetchClobMarketsFromHub(signal),
		staleTime: 30_000,
		refetchOnWindowFocus: true,
		retry: 2,
	});
	const markets: ClobMarketConfig[] = useMemo(() => {
		if (marketsQ.data && marketsQ.data.length > 0) return marketsQ.data;
		if (marketsQ.isError || marketsQ.isFetched) return getClobMarketsFromVite();
		return [];
	}, [marketsQ.data, marketsQ.isError, marketsQ.isFetched]);

	const [marketId, setMarketId] = useState('dgc-data');
	useEffect(() => {
		if (markets.length === 0) return;
		if (!markets.some((m) => (m.marketId ?? m.orderBookId) === marketId)) {
			setMarketId(markets[0].marketId ?? markets[0].orderBookId);
		}
	}, [markets, marketId]);

	const config: ClobMarketConfig | null = useMemo(
		() => markets.find((m) => (m.marketId ?? m.orderBookId) === marketId) ?? markets[0] ?? null,
		[markets, marketId],
	);
	const baseType = config ? clobBaseType(config) : '';
	const baseSymbol = config?.baseSymbol ?? 'DGC';
	const quoteSymbol = config?.quoteSymbol ?? 'DATA';
	const lotSizeMist = useMemo(() => getClobLotSizeMist(), []);
	const defaultRef = config?.referencePrice ?? DATA_DGC_REFERENCE_DATA_PER_DGC;

	const pushWsUrl = useMemo(() => getPushHubWsUrl(), []);
	const client = useSuiClient();
	const account = useCurrentAccount();
	const queryClient = useQueryClient();
	const { mutateAsync: signAndExecute, isPending: submitting } =
		useCpcSignAndExecuteTransaction();

	const [bottomTab, setBottomTab] = useState<BottomTab>('open');
	const [klineInterval, setKlineInterval] = useState<KlineInterval>('1m');
	const [bidPrice, setBidPrice] = useState('');
	const [askPrice, setAskPrice] = useState('');
	const [bidQty, setBidQty] = useState('');
	const [askQty, setAskQty] = useState('');
	const [cancellingId, setCancellingId] = useState<string>();

	useEffect(() => {
		setBidPrice('');
		setAskPrice('');
		setBidQty('');
		setAskQty('');
	}, [marketId]);

	const pickPrice = useCallback((dataPerBase: string) => {
		const p = sanitizeLimitPriceInput(dataPerBase);
		setBidPrice(p);
		setAskPrice(p);
	}, []);

	const refreshAccount = useCallback(() => {
		void queryClient.invalidateQueries({ queryKey: ['cpc-trade-account'] });
		void queryClient.invalidateQueries({ queryKey: ['cpc-push-hub-orders'] });
	}, [queryClient]);

	const push = usePushHub(pushWsUrl, !!config, {
		klineInterval,
		depthLimit: DEPTH_MAX_LEVELS,
		tradeLimit: 100,
		pair: config?.hubPair,
		owner: account?.address,
	});

	const accountQ = useQuery({
		queryKey: ['cpc-trade-account', config?.orderBookId, baseType, account?.address],
		enabled: !!config,
		queryFn: async () => {
			if (!config) throw new Error('no config');
			const reachable = await verifyClobObjectsReachable(client, config);
			let baseBal = '';
			let dataBal = '';
			if (account?.address) {
				const [baseView, data] = await Promise.all([
					baseType.includes('::dgc::DGC')
						? getCpcBalance(client, account.address).then((b) => ({
								text: mistToDgc(b.totalMist),
							}))
						: getCoinBalanceView(client, account.address, baseType).then((b) => ({
								text: mistToCoin(b.totalMist, 9),
							})),
					getCoinBalanceView(client, account.address, config.dataTypeTag),
				]);
				baseBal = baseView.text;
				dataBal = mistToCoin(data.totalMist, 9);
			}
			return { reachable, cpcBal: baseBal, dataBal };
		},
		refetchInterval: 30_000,
		staleTime: 5_000,
	});

	const hubPair = config?.hubPair;
	const pairId = hubPair === 'CPC-DATA' ? 2 : 1;
	const openOrders = useMemo(
		() => filterHubOrdersForPair(push.openOrders, pairId),
		[push.openOrders, pairId],
	);
	const orderHistory = useMemo(
		() => filterHubOrdersForPair(push.historyOrders, pairId),
		[push.historyOrders, pairId],
	);

	const marketTrades = push.trades;
	const myTrades = useMemo(
		() =>
			account?.address ? filterMyDataCpcTrades(marketTrades, account.address) : [],
		[marketTrades, account?.address],
	);

	const lastTradeRaw = marketTrades[0]?.dataPerDgc;
	const lastKline = lastAt(push.klines);
	const markPrice =
		formatLastTradePrice(lastTradeRaw, '') ||
		formatLastTradePrice(lastKline?.close, '') ||
		formatLastTradePrice(defaultRef);

	const refPrice = markPrice;
	const displayPrice = markPrice;
	const displayPriceNum =
		Number.parseFloat(lastTradeRaw ?? '') ||
		lastKline?.close ||
		Number.parseFloat(defaultRef) ||
		0;

	const ticker24h = useMemo(
		() => computeTicker24h(marketTrades, displayPriceNum, lotSizeMist),
		[marketTrades, displayPriceNum, lotSizeMist],
	);

	const klineChange = useMemo(() => {
		const first = push.klines[0]?.open;
		const last = lastAt(push.klines)?.close;
		if (first == null || last == null) return ticker24h.changePct;
		return pctChange(first, last);
	}, [push.klines, ticker24h.changePct]);

	const bidEstimate = useMemo(() => {
		const qty = tryParseQtyLots(bidQty, lotSizeMist);
		if (!qty) return `预计消耗 — ${quoteSymbol}`;
		try {
			const ticks = parseLimitPriceDgcPerData(bidPrice.trim() || refPrice);
			return `预计消耗 ${mistToDgc(quoteDgcMistForDataBid(ticks, qty, lotSizeMist))} ${quoteSymbol}`;
		} catch {
			return '限价格式无效';
		}
	}, [bidQty, bidPrice, refPrice, quoteSymbol, lotSizeMist]);

	const askEstimate = useMemo(() => {
		const qty = tryParseQtyLots(askQty, lotSizeMist);
		if (!qty) return `预计收到 — ${quoteSymbol}（扣费后）`;
		try {
			const ticks = parseLimitPriceDgcPerData(askPrice.trim() || refPrice);
			return `预计收到 ${mistToDgc(estimateCpcProceedsForDataAsk(ticks, qty, 30n, lotSizeMist))} ${quoteSymbol}（扣费后）`;
		} catch {
			return '限价格式无效';
		}
	}, [askQty, askPrice, refPrice, quoteSymbol, lotSizeMist]);

	const submitOrder = async (side: TradeSide) => {
		if (!config) {
			toast.error('未配置 CLOB');
			return;
		}
		if (!account) {
			toast.error('请先连接 DGC Wallet');
			return;
		}
		const qtyRaw = side === 'bid' ? bidQty.trim() : askQty.trim();
		const priceRaw = side === 'bid' ? bidPrice.trim() : askPrice.trim();
		if (!qtyRaw) {
			toast.error(`请输入 ${baseSymbol} 数量`);
			return;
		}
		let qtyLots: bigint;
		try {
			qtyLots = parseQtyLots(qtyRaw, lotSizeMist);
		} catch (e) {
			const code = e instanceof Error ? e.message : '';
			toast.error(
				code === 'not_lot'
					? `${baseSymbol} 数量须为整手（当前一手 = ${mistToDgc(lotSizeMist)} ${baseSymbol}）`
					: `${baseSymbol} 数量须大于 0`,
			);
			return;
		}
		let priceTicks: bigint;
		try {
			priceTicks = parseLimitPriceDgcPerData(priceRaw || refPrice);
		} catch {
			toast.error('限价格式无效');
			return;
		}
		try {
			const tx =
				side === 'bid'
					? await buildPlaceBidForOwner(client, account.address, {
							packageId: config.packageId,
							orderBookId: config.orderBookId,
							registryId: config.registryId,
							baseType,
							quoteType: config.dataTypeTag,
							quoteAmountMist: quoteDgcMistForDataBid(priceTicks, qtyLots, lotSizeMist),
							priceTicks,
							qtyLots,
						})
					: await buildPlaceAskForOwner(client, account.address, {
							packageId: config.packageId,
							orderBookId: config.orderBookId,
							registryId: config.registryId,
							baseType,
							quoteType: config.dataTypeTag,
							priceTicks,
							qtyLots,
							lotSizeMist,
						});
			tx.setSenderIfNotSet(account.address);
			const result = await signAndExecute({ transaction: tx });
			toast.success(side === 'bid' ? '买单已提交' : '卖单已提交');
			if (result.digest) toast(`Tx: ${result.digest.slice(0, 16)}…`, { icon: 'ℹ️' });
			if (side === 'bid') setBidQty('');
			else setAskQty('');
			refreshAccount();
			void queryClient.invalidateQueries({ queryKey: ['hub-clob-markets'] });
			void queryClient.invalidateQueries({ queryKey: ['cpc-push-hub-orders'] });
		} catch (e) {
			const raw = e instanceof Error ? e.message : String(e);
			const m =
				/Available amount in account.*?is less than requested:\s*(\d+)\s*<\s*(\d+)/.exec(raw);
			let msg = raw;
			if (m) {
				const available = BigInt(m[1]!);
				const requested = BigInt(m[2]!);
				const diff = requested - available;
				const assetHint =
					side === 'bid'
						? `买入 ${baseSymbol} 扣的是 ${quoteSymbol}（报价），Gas 用 DGC。有 ${baseSymbol} 余额不够，需要 ${quoteSymbol}。`
						: `卖单扣 ${baseSymbol} + Gas；请减少卖量留 Gas。`;
				msg =
					`${side === 'bid' ? '买单' : '卖单'}数量 ${mistToDgc(qtyLots * lotSizeMist)} ${baseSymbol}。` +
					`链上累计预扣：可用 ${mistToDgc(available)}，需要 ${mistToDgc(requested)}` +
					`（差额约 ${mistToDgc(diff)}）。${assetHint}`;
			} else if (side === 'bid' && /DATA 不足/i.test(raw)) {
				msg = `${raw}（买入 ${baseSymbol} 花费的是 ${quoteSymbol}，不是 ${baseSymbol}）`;
			}
			toast.error(/transaction expired/i.test(msg) ? '交易已过期，请重试' : msg);
		}
	};

	const depthBids: BookLevel[] = push.bids;
	const depthAsks: BookLevel[] = push.asks;

	const bookTicker = useMemo(
		() => computeBookTicker(depthBids, depthAsks),
		[depthBids, depthAsks],
	);

	const onCancelOrder = async (orderId: string) => {
		if (!config || !account) return;
		setCancellingId(orderId);
		try {
			const tx = buildCancelOrder({
				packageId: config.packageId,
				orderBookId: config.orderBookId,
				registryId: config.registryId,
				baseType,
				quoteType: config.dataTypeTag,
				orderId,
			});
			tx.setSenderIfNotSet(account.address);
			await signAndExecute({ transaction: tx });
			toast.success('撤单已提交');
			refreshAccount();
		} catch (e) {
			toast.error(e instanceof Error ? e.message : String(e));
		} finally {
			setCancellingId(undefined);
		}
	};

	if (marketsQ.isLoading && !config) {
		return (
			<PageLayout
				content={
					<div className="mx-auto max-w-xl py-8 text-center">
						<Text variant="heading/h1" color="steel-darker">
							现货交易
						</Text>
						<Text variant="body/medium" color="steel-dark" className="mt-4">
							正在从 Push Hub 加载交易对…
						</Text>
					</div>
				}
			/>
		);
	}

	if (!config) {
		return (
			<PageLayout
				content={
					<div className="mx-auto max-w-xl py-8 text-center">
						<Text variant="heading/h1" color="steel-darker">
							现货交易
						</Text>
						<Text variant="body/medium" color="steel-dark" className="mt-4">
							Hub 未发布 CLOB 配置（/v1/stack-config 无 clob）。请在链上注册市场后
							publish_cpc_stack_config，再刷新本页。
						</Text>
						<Link to="/" className={`mt-6 inline-block ${tradeCls.link}`}>
							← 返回首页
						</Link>
					</div>
				}
			/>
		);
	}

	const changeUp = klineChange == null || klineChange >= 0;

	const marketOverview = (
		<div className="flex flex-col gap-2">
			{markets.length > 1 ? (
				<div className="flex flex-wrap gap-2">
					{markets.map((m) => {
						const id = m.marketId ?? m.orderBookId;
						const label = `${m.baseSymbol ?? '?'} / ${m.quoteSymbol ?? 'DATA'}`;
						const active = id === (config.marketId ?? config.orderBookId);
						return (
							<button
								key={id}
								type="button"
								onClick={() => setMarketId(id)}
								className={clsx(
									'rounded-lg px-3 py-1.5 text-sm font-semibold transition',
									active
										? 'bg-white/15 text-white ring-1 ring-white/30'
										: 'bg-white/5 text-[#aeb9d0] hover:bg-white/10',
								)}
							>
								{label}
							</button>
						);
					})}
				</div>
			) : null}
			<TradeMarketOverview
				baseSymbol={baseSymbol}
				quoteSymbol={quoteSymbol}
				displayPrice={displayPrice}
				changeUp={changeUp}
				klineChange={klineChange}
				ticker24h={ticker24h}
				bookTicker={bookTicker}
				live={push.connected}
				cpcBal={accountQ.data?.cpcBal ?? ''}
				dataBal={accountQ.data?.dataBal ?? ''}
				showBalances={!!account}
			/>
			<CpcTradeAssetsPanel market={config} />
		</div>
	);

	return (
		<PageLayout
			gradient={{ size: 'md', content: marketOverview }}
			content={
				<div className="space-y-2">
					<div className="grid grid-cols-1 gap-2 lg:grid-cols-[minmax(200px,240px)_minmax(0,1fr)_minmax(200px,240px)] lg:grid-rows-[auto_auto] lg:items-start">
						<div className="lg:col-start-1 lg:row-span-2 lg:row-start-1">
							<TradeOrderBook
								bids={depthBids}
								asks={depthAsks}
								markPrice={markPrice}
								lastTradeSide={resolveTradeDisplaySide(
									marketTrades[0] ?? { dgcPerData: '', tradeSide: 'unknown' },
									marketTrades[1],
								)}
								onPickPrice={pickPrice}
								baseSymbol={baseSymbol}
								quoteSymbol={quoteSymbol}
							/>
						</div>

						<div className="flex min-w-0 flex-col gap-1 lg:col-start-2 lg:row-span-2 lg:row-start-1">
							<CpcKlineChart
								candles={push.klines}
								interval={klineInterval}
								onIntervalChange={setKlineInterval}
								height={KLINE_CHART_HEIGHT_PX}
								hubLive={push.connected}
							/>
							<TradeOrderForm
								bidPrice={bidPrice}
								askPrice={askPrice}
								onBidPriceChange={(v) => setBidPrice(sanitizeLimitPriceInput(v))}
								onAskPriceChange={(v) => setAskPrice(sanitizeLimitPriceInput(v))}
								bidQty={bidQty}
								askQty={askQty}
								onBidQtyChange={(v) => setBidQty(sanitizeQtyInput(v))}
								onAskQtyChange={(v) => setAskQty(sanitizeQtyInput(v))}
								bidEstimate={bidEstimate}
								askEstimate={askEstimate}
								refPrice={refPrice}
								cpcBal={accountQ.data?.cpcBal ?? ''}
								dataBal={accountQ.data?.dataBal ?? ''}
								submitting={submitting}
								hasAccount={!!account}
								onSubmitBid={() => void submitOrder('bid')}
								onSubmitAsk={() => void submitOrder('ask')}
								baseSymbol={baseSymbol}
								quoteSymbol={quoteSymbol}
								lotSizeMist={lotSizeMist}
							/>
						</div>

						<div className="lg:col-start-3 lg:row-span-2 lg:row-start-1">
							<TradeRecentTrades
								rows={marketTrades}
								viewerAddress={account?.address}
								baseSymbol={baseSymbol}
							/>
						</div>
					</div>

					<TradeBottomPanel
						tab={bottomTab}
						onTabChange={setBottomTab}
						openOrders={openOrders}
						orderHistory={orderHistory}
						marketTrades={marketTrades}
						myTrades={myTrades}
						viewerAddress={account?.address}
						onCancelOrder={(id) => void onCancelOrder(id)}
						cancellingId={cancellingId}
					/>

					{!push.connected && (
						<Text variant="bodySmall/medium" color="issue-dark" className="text-center">
							{push.error
								? `Push Hub 连线异常：${push.error}`
								: 'Push Hub 连线中…（若长时间 Offline，检查 wss://dgclabs.cc/wss）'}
						</Text>
					)}
					{accountQ.data && !accountQ.data.reachable && (
						<Text variant="bodySmall/medium" color="issue-dark" className="text-center">
							OrderBook 在 RPC 上不可达
						</Text>
					)}
				</div>
			}
		/>
	);
}
